/**
 * The owner's live feed for pinned frames (plan 12 §4): each widget gets its
 * frame's full detail on `ready` and again on every socket update for it;
 * a deleted or archived frame is unpinned. The events that change a pinned
 * project's stores (tasks, memory) are relayed to the widgets too
 * (`desktop-pin-project-data.ts`).
 *
 * `routeDesktopPinEvent` is also the one gate for what the owner's extra
 * project subscriptions add (`owner-extra-projects.ts`): a pin-only
 * project's events feed only the widgets; Denker's projects (Home, the
 * picked target's) reach the chat and cursors, never the viewed wall.
 * Questions come through the user's own group and always pass.
 */
import { emitTo } from "@tauri-apps/api/event";
import { isNotFoundError } from "@/api/client";
import { getFrame } from "@/api/frame-listing";
import type { Frame } from "@/api/spaces";
import type { WsMessage } from "@/api/websocket";
import {
  frameDataFromApi,
  frameDataPatchFromApi,
  mergeFrameData,
  type FrameData,
} from "@/lib/frame-data";
import { isTauri } from "@/lib/is-tauri";
import { isOffWallFrameType } from "@/lib/wall-membership";
import type { OwnerExtraProjects } from "@/desktop/owner-extra-projects";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import {
  DESKTOP_PIN_EVENTS,
  pinWindowLabel,
  type DesktopPinFramePayload,
} from "./desktop-pin-contract";
import {
  DESKTOP_PIN_PROJECT_EVENT,
  pinProjectEventStore,
  pinProjectStores,
  type DesktopPinProjectEventPayload,
} from "./desktop-pin-project-data";
import {
  desktopPinFor,
  readDesktopPins,
  removeDesktopPin,
} from "./desktop-pins-store";

const FRAME_EVENTS: ReadonlySet<string> = new Set([
  "frame.creating",
  "frame.created",
  "frame.updated",
  "frame.deleted",
]);

/**
 * What a project subscription adds to the owner socket: the event types the
 * server sends only to a project's group and its legacy space groups. Every
 * other type, agent questions included, reaches the owner through the
 * user's own group whatever it subscribes, so the Workspace keeps hearing
 * it for every project.
 */
const PROJECT_GROUP_EVENTS: ReadonlySet<string> = new Set([
  ...FRAME_EVENTS,
  "cursor.move",
  "execution.resource",
]);

/** How long a frame sent to its widget is re-sent on `ready` without a fetch. */
export const PIN_FRAME_FRESH_MS = 10_000;

/**
 * How long a deleted frame's pin waits before it is checked: a move to
 * another project also sends `frame.deleted`, and the move's own response
 * retargets the pin (`retargetDesktopPin`) within this time.
 */
export const DELETED_PIN_RECHECK_MS = 1500;

/** Executions remembered per owner; the oldest goes first. */
const MAX_EXTRA_EXECUTIONS = 500;

/** The last frame each widget was sent (socket patches merge into it). */
const sent = new Map<string, { frame: FrameData; fetchedAt: number }>();
/** One detail request per frame however many ask while it runs. */
const loading = new Map<string, Promise<void>>();
/** The deleted frames waiting for their check. */
const rechecks = new Map<string, ReturnType<typeof setTimeout>>();
/** The projects the owner hears besides the viewed one. */
let extraProjects: OwnerExtraProjects | null = null;
/**
 * The extra projects' executions, learnt from their events that name the
 * project, for the legacy space-group copies that name none.
 */
const extraExecutions = new Map<string, string>();

/** Send the frame to its widget with the pin's current project. */
function send(frame: FrameData, fetchedAt: number): void {
  if (frame.archivedAt) {
    removeDesktopPin(frame.frameId);
    return;
  }
  const pin = desktopPinFor(frame.frameId);
  if (!pin) return;
  sent.set(frame.frameId, { frame, fetchedAt });
  const payload: DesktopPinFramePayload = {
    frameId: frame.frameId,
    projectId: pin.projectId,
    frame,
  };
  void emitTo(
    pinWindowLabel(frame.frameId),
    DESKTOP_PIN_EVENTS.frame,
    payload,
  ).catch(() => {});
}

/**
 * Fetch the pinned frame's detail from the pin's project and send it. A
 * frame that project no longer has, or an archived one, is unpinned; a pin
 * retargeted meanwhile ignores the answer; another failure leaves the
 * widget as it is until its next `ready`.
 */
function fetchPinnedFrame(frameId: string): Promise<void> {
  const pin = desktopPinFor(frameId);
  if (!pin) return Promise.resolve();
  const { projectId } = pin;
  const stillPinned = () => desktopPinFor(frameId)?.projectId === projectId;
  const request: Promise<void> = getFrame(projectId, frameId)
    .then((frame) => {
      if (stillPinned()) send(frameDataFromApi(frame), Date.now());
    })
    .catch((error: unknown) => {
      if (isNotFoundError(error) && stillPinned()) removeDesktopPin(frameId);
    })
    .finally(() => {
      if (loading.get(frameId) === request) loading.delete(frameId);
    });
  loading.set(frameId, request);
  return request;
}

/** Send the pinned frame, joining a fetch already under way. */
export function relayPinnedFrame(frameId: string): Promise<void> {
  return loading.get(frameId) ?? fetchPinnedFrame(frameId);
}

/** The widget asked for its frame: the copy it was just sent, else a fetch. */
export function answerPinnedFrame(frameId: string): Promise<void> {
  const last = sent.get(frameId);
  if (
    last &&
    !loading.has(frameId) &&
    Date.now() - last.fetchedAt < PIN_FRAME_FRESH_MS
  ) {
    send(last.frame, last.fetchedAt);
    return Promise.resolve();
  }
  return relayPinnedFrame(frameId);
}

function cancelRecheck(frameId: string): void {
  const timer = rechecks.get(frameId);
  if (timer) clearTimeout(timer);
  rechecks.delete(frameId);
}

/** The widget closed: nothing more is sent to it. */
export function forgetPinnedFrame(frameId: string): void {
  sent.delete(frameId);
  cancelRecheck(frameId);
}

/** The owner let go: another owner relays from scratch. */
export function clearPinRelay(): void {
  sent.clear();
  for (const timer of rechecks.values()) clearTimeout(timer);
  rechecks.clear();
}

/** The extra projects the owner subscribed (`owner-extra-projects.ts`). */
export function setOwnerExtraProjects(
  projects: OwnerExtraProjects | null,
): void {
  extraProjects = projects;
  // A teardown keeps what was learnt: the setup that follows it prunes.
  if (!projects) return;
  for (const [executionId, projectId] of extraExecutions) {
    if (!extraProjectRoute(projectId)) extraExecutions.delete(executionId);
  }
}

function stringField(
  data: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value) return value;
  }
  return undefined;
}

/**
 * How an extra project's events reach the Workspace: Denker's (Home, the
 * target's) in part, a presented card's project only for the cards held, a
 * pin-only project's not at all; null for the viewed project and any
 * project not heard.
 */
function extraProjectRoute(
  projectId: string | undefined,
): "denker" | "shown" | "pins" | null {
  if (
    !projectId ||
    !extraProjects ||
    projectId === useProjectStore.getState().currentProject?.id
  )
    return null;
  if (
    projectId === extraProjects.home ||
    projectId === extraProjects.targetProject
  )
    return "denker";
  if (extraProjects.shown.has(projectId)) return "shown";
  return extraProjects.pins.has(projectId) ? "pins" : null;
}

/** A frame event the chat reads: an off-wall frame, or one already held. */
function isChatFrameEvent(
  msg: WsMessage,
  frameId: string | undefined,
): boolean {
  const frameType = stringField(msg.data, "frame_type");
  return (
    (frameType !== undefined && isOffWallFrameType(frameType)) ||
    (frameId !== undefined && !!useFramesStore.getState().framesById[frameId])
  );
}

function rememberExecution(executionId: string, projectId: string): void {
  extraExecutions.delete(executionId);
  extraExecutions.set(executionId, projectId);
  if (extraExecutions.size <= MAX_EXTRA_EXECUTIONS) return;
  const oldest = extraExecutions.keys().next().value;
  if (oldest !== undefined) extraExecutions.delete(oldest);
}

/**
 * A deleted frame is checked again after a moment, by a fetch of its own
 * once any under way settled: gone from the pin's project (its new one
 * after a move) → unpinned, else re-sent. An update merges into what the
 * widget has and is sent.
 */
function relayFrameEvent(msg: WsMessage, frameId: string): void {
  if (msg.type === "frame.deleted") {
    cancelRecheck(frameId);
    const timer = setTimeout(() => {
      rechecks.delete(frameId);
      const pending = loading.get(frameId) ?? Promise.resolve();
      void pending.then(() => fetchPinnedFrame(frameId));
    }, DELETED_PIN_RECHECK_MS);
    rechecks.set(frameId, timer);
    return;
  }
  const previous = sent.get(frameId);
  if (!previous) return;
  const next = mergeFrameData(
    previous.frame,
    frameDataPatchFromApi(msg.data as Partial<Frame>),
  );
  if (next !== previous.frame) send(next, previous.fetchedAt);
}

/**
 * Send a store-changing event to each widget that reads that store and
 * shows its project (any project for an event naming none). A widget whose
 * frame has not been sent yet loads the store with its frame.
 */
function relayProjectEvent(msg: WsMessage, project: string | undefined): void {
  const store = pinProjectEventStore(msg);
  if (!store) return;
  const payload: DesktopPinProjectEventPayload = {
    projectId: project ?? null,
    message: { type: msg.type, data: msg.data },
  };
  for (const pin of readDesktopPins()) {
    if (project !== undefined && pin.projectId !== project) continue;
    const frameType = sent.get(pin.frameId)?.frame.frameType;
    if (!pinProjectStores(frameType).includes(store)) continue;
    void emitTo(
      pinWindowLabel(pin.frameId),
      DESKTOP_PIN_PROJECT_EVENT,
      payload,
    ).catch(() => {});
  }
}

/**
 * Called for every owner socket message before the Workspace applies it.
 * On the desktop a frame event for a pinned frame is relayed to its widget,
 * a store-changing event to the widgets of its project. True when the
 * Workspace must not apply the event: one an extra project's subscription
 * added, except Denker's projects' cursors, resources and chat frames. Its project is the one it names, else its execution's, else
 * (frame events) the pin's. The server drops the legacy space fields of a
 * subscribed project's events, so only project fields are left.
 */
export function routeDesktopPinEvent(msg: WsMessage): boolean {
  const named = stringField(msg.data, "project_id", "project");
  const executionId = stringField(msg.data, "execution_id");
  if (executionId && named && extraProjectRoute(named))
    rememberExecution(executionId, named);
  let project =
    named ?? (executionId ? extraExecutions.get(executionId) : undefined);
  const frameId = FRAME_EVENTS.has(msg.type)
    ? stringField(msg.data, "id", "frame_id")
    : undefined;
  if (isTauri()) {
    const pin = frameId ? desktopPinFor(frameId) : undefined;
    if (frameId && pin) relayFrameEvent(msg, frameId);
    project ??= pin?.projectId;
    relayProjectEvent(msg, project);
  }
  if (!PROJECT_GROUP_EVENTS.has(msg.type)) return false;
  const route = extraProjectRoute(project);
  if (route === "denker")
    return FRAME_EVENTS.has(msg.type) && !isChatFrameEvent(msg, frameId);
  if (route === "shown")
    return !(
      FRAME_EVENTS.has(msg.type) &&
      frameId !== undefined &&
      !!useFramesStore.getState().framesById[frameId]
    );
  return route === "pins";
}
